import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, FileText, Database, Lock, Globe, Users,
  RefreshCw, Mail, ChevronRight, Eye, Cookie, Server, Smartphone, MessageSquare, AlertTriangle
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { contactInfo } from '../data/mock';

const PrivacyPolicyPage = () => {
  const [activeSection, setActiveSection] = useState('');

  // Table of contents sections
  const sections = [
    { id: 'information-collect', title: 'Information We Collect', icon: Database },
    { id: 'how-we-use', title: 'How We Use Your Information', icon: Eye },
    { id: 'sms-terms', title: 'SMS Terms', icon: MessageSquare },
    { id: 'sms-communications', title: 'SMS Communications', icon: MessageSquare },
    { id: 'carrier-liability', title: 'Carrier Liability Disclaimer', icon: AlertTriangle },
    { id: 'protection', title: 'How We Protect Your Information', icon: Lock },
    { id: 'third-party', title: 'Information Disclosure to Third Parties', icon: Server },
    { id: 'third-party-links', title: 'Third-Party Links and Services', icon: Globe },
    { id: 'remarketing', title: 'Remarketing', icon: RefreshCw },
    { id: 'a2p-compliance', title: 'A2P Compliance', icon: Smartphone },
    { id: 'coppa', title: 'COPPA Compliance', icon: Users },
    { id: 'personal-info', title: 'Personal, Financial, and Property Information', icon: FileText },
    { id: 'online-only', title: 'Online Privacy Policy Only', icon: Globe },
    { id: 'changes', title: 'Changes to Our Privacy Policy', icon: RefreshCw },
    { id: 'contact', title: 'Contacting Us', icon: Mail }
  ];

  // Track scroll position for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy | ASAP Capital</title>
        <meta 
          name="description" 
          content="ASAP Capital's privacy policy explaining how we collect, use, and protect your personal information." 
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="/privacy" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-12 lg:pt-44 lg:pb-16 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]" />
            
            <div className="relative z-10 max-w-[900px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 bg-white/5 border border-white/10">
                  <Shield className="w-4 h-4 text-white/60" />
                  <span className="text-xs font-medium text-white/60 uppercase tracking-wider">Legal</span>
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6">
                  Website Privacy Policy
                </h1>

                <div className="text-white/60 text-base lg:text-lg leading-relaxed space-y-4">
                  <p>
                    <strong className="text-white">Effective Date:</strong> 02/03/2026
                  </p>
                  <p>
                    Welcome to ASAP Capital ("the Website"). Your privacy is very important to us, and we are committed to protecting the personal information you share with us when using our services. This Privacy Policy explains what information we collect, how we use it, and how we protect your data. By accessing or using the Website, you agree to the terms outlined in this policy.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MAIN CONTENT WITH TOC ==================== */}
          <section className="py-12 lg:py-16 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <div className="grid lg:grid-cols-[280px_1fr] gap-12">
                
                {/* Sticky Table of Contents - Desktop Only */}
                <div className="hidden lg:block">
                  <div className="sticky top-32">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-4">On This Page</p>
                    <nav className="space-y-1">
                      {sections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-all ${
                            activeSection === section.id
                              ? 'bg-white/10 text-white border-l-2 border-[#D4AF37]'
                              : 'text-white/50 hover:text-white/70 hover:bg-white/5'
                          }`}
                        >
                          <section.icon className="h-4 w-4 flex-shrink-0" />
                          <span className="line-clamp-1">{section.title}</span>
                        </button>
                      ))}
                    </nav>

                    <div className="mt-8 pt-6 border-t border-white/10">
                      <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Related Pages</p>
                      <div className="space-y-2">
                        <Link 
                          to="/terms"
                          className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm transition-colors"
                        >
                          <ChevronRight className="h-3 w-3" />
                          Terms of Service
                        </Link>
                        <Link 
                          to="/disclosures"
                          className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm transition-colors"
                        >
                          <ChevronRight className="h-3 w-3" />
                          Disclosures
                        </Link>
                        <Link 
                          to="/contact"
                          className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm transition-colors"
                        >
                          <ChevronRight className="h-3 w-3" />
                          Contact Us
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className="max-w-[700px]">
                  {/* Information We Collect */}
                  <section id="information-collect" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Database className="h-6 w-6 text-[#D4AF37]" />
                        Information We Collect
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>We collect the following types of information when you interact with our Website:</p>
                        
                        <div className="mt-4">
                          <h4 className="text-white font-semibold mb-2">Information You Provide:</h4>
                          <p>
                            When you fill out forms, sign up for newsletters, or make inquiries, we may collect personal details such as your name, email address, phone number, and other contact information.
                          </p>
                        </div>

                        <div className="mt-4">
                          <h4 className="text-white font-semibold mb-2">Website Usage Data:</h4>
                          <p>
                            We collect non-personal data through automated tools such as Google Analytics. This data includes your IP address, browser type, device information, and browsing activity, which help us enhance your experience on our Website. This information is anonymous and not linked to any individual.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </section>

                  {/* How We Use Your Information */}
                  <section id="how-we-use" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Eye className="h-6 w-6 text-[#D4AF37]" />
                        How We Use Your Information
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>We use your information for the following purposes:</p>
                        
                        <div className="mt-4">
                          <h4 className="text-white font-semibold mb-2">To Process Transactions:</h4>
                          <p>
                            Your information may be used to fulfill the services or products you request. We do not share, sell, or trade your contact information, including your phone number or mobile information, without your consent.
                          </p>
                          <p className="mt-2">
                            Your contact information, including your phone number and mobile information, will not be shared or sold to any third party.
                          </p>
                        </div>

                        <div className="mt-4">
                          <h4 className="text-white font-semibold mb-2">To Send Communication:</h4>
                          <p>
                            We may use your email address to respond to inquiries, provide requested information, and send updates related to your interests.
                          </p>
                        </div>

                        <div className="mt-4">
                          <h4 className="text-white font-semibold mb-2">To Send Marketing Communications:</h4>
                          <p>
                            We may use your phone number for sending marketing text messages regarding updates or offers related to our services. You can opt out of these messages at any time by texting "STOP."
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </section>

                  {/* SMS Terms */}
                  <section id="sms-terms" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <MessageSquare className="h-6 w-6 text-[#D4AF37]" />
                        SMS Terms
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          By providing your phone number and opting in through our website or other forms, you consent to receive SMS (text) messages from ASAP Capital related to your inquiry or services. Message frequency may vary. Message and data rates may apply.
                        </p>
                        <p>
                          You may opt out of SMS messages at any time by replying STOP. For assistance, reply HELP.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* SMS Communications */}
                  <section id="sms-communications" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <MessageSquare className="h-6 w-6 text-[#D4AF37]" />
                        SMS Communications
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          If you provide your phone number, ASAP Capital may send you SMS (text) messages related to your inquiry, appointments, or services. Message and data rates may apply. You may opt out of SMS communications at any time by replying STOP. For assistance, reply HELP.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Carrier Liability Disclaimer */}
                  <section id="carrier-liability" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <AlertTriangle className="h-6 w-6 text-[#D4AF37]" />
                        Carrier Liability Disclaimer
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          Carriers are not responsible for delayed or undelivered messages. ASAP Capital is not liable for any delays, failures, or errors in message delivery caused by mobile carriers or network issues. Message and data rates may apply based on the user's mobile carrier plan.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* How We Protect Your Information */}
                  <section id="protection" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Lock className="h-6 w-6 text-[#D4AF37]" />
                        How We Protect Your Information
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          We implement industry-standard security measures to protect your personal information during its collection and transmission. Although no system is entirely secure, we strive to ensure your data remains protected.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Information Disclosure to Third Parties */}
                  <section id="third-party" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Server className="h-6 w-6 text-[#D4AF37]" />
                        Information Disclosure to Third Parties
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. Information sharing to subcontractors in support services, such as customer service, is permitted. All other use case categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
                        </p>
                        <p>
                          We may share information with trusted service providers (for example, website hosting, analytics, customer support, or scheduling tools) solely to operate the Website and deliver services to you. These providers are required to keep your information confidential and use it only for the services they provide to us.
                        </p>
                        <p>
                          We may also disclose information if required by law or to protect our rights, property, or safety.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Third-Party Links and Services */}
                  <section id="third-party-links" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Globe className="h-6 w-6 text-[#D4AF37]" />
                        Third-Party Links and Services
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          Our Website may contain links to third-party sites. These sites have their own privacy policies, and we are not responsible for their content or activities. Please review their privacy policies before interacting with those sites.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Remarketing */}
                  <section id="remarketing" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <RefreshCw className="h-6 w-6 text-[#D4AF37]" />
                        Remarketing
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          We may use third-party vendors, including Google, to show ads to previous visitors. These ads help remind users of our services and offer special promotions. This remarketing does not involve the collection of personally identifiable information.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* A2P Compliance */}
                  <section id="a2p-compliance" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Smartphone className="h-6 w-6 text-[#D4AF37]" />
                        A2P Compliance
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          We comply with applicable regulations, including the Telephone Consumer Protection Act (TCPA) and other applicable laws related to Automated Text Messaging (A2P). By providing your phone number, you agree to receive communications from us via automated methods (including text messages). You may opt out at any time by texting "STOP."
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* COPPA Compliance */}
                  <section id="coppa" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Users className="h-6 w-6 text-[#D4AF37]" />
                        Children's Online Privacy Protection Act (COPPA) Compliance
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          We comply with COPPA and do not collect personal information from anyone under 13 years of age. Our services are intended for users who are at least 13 years old.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Personal, Financial, and Property Information */}
                  <section id="personal-info" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <FileText className="h-6 w-6 text-[#D4AF37]" />
                        Personal, Financial, and Property Information
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          If you are submitting information such as contact details, rest assured that this information is used solely for your specific request. We do not share this information with third parties unless necessary to provide services to you.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Online Privacy Policy Only */}
                  <section id="online-only" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Globe className="h-6 w-6 text-[#D4AF37]" />
                        Online Privacy Policy Only
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          This privacy policy applies only to information collected through our Website and does not cover information collected offline or via other channels.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Changes to Our Privacy Policy */}
                  <section id="changes" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <RefreshCw className="h-6 w-6 text-[#D4AF37]" />
                        Changes to Our Privacy Policy
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          We may update our privacy policy from time to time. Any changes will be posted on this page with an updated "Effective Date."
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Contacting Us */}
                  <section id="contact" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Mail className="h-6 w-6 text-[#D4AF37]" />
                        Contacting Us
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          If you have any questions or concerns about this Privacy Policy, please contact us using the information provided on our Contact page.
                        </p>
                        <div className="bg-white/5 border border-white/10 rounded-xl p-5 mt-4">
                          <p className="text-white font-semibold mb-3">ASAP Capital</p>
                          <p>{contactInfo.address.city}, {contactInfo.address.state}</p>
                          <p>United States</p>
                          <p className="mt-3">
                            <strong className="text-white/70">Email:</strong>{' '}
                            <a 
                              href={`mailto:${contactInfo.email}`} 
                              className="text-[#D4AF37] hover:text-[#F5D76E] transition-colors"
                            >
                              {contactInfo.email}
                            </a>
                          </p>
                          <p>
                            <strong className="text-white/70">Phone:</strong>{' '}
                            <a 
                              href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}
                              className="text-[#D4AF37] hover:text-[#F5D76E] transition-colors"
                            >
                              {contactInfo.phone}
                            </a>
                          </p>
                        </div>
                        <p className="mt-4">
                          You may also visit our Contact page to submit inquiries:{' '}
                          <Link 
                            to="/contact" 
                            className="text-[#D4AF37] hover:text-[#F5D76E] transition-colors"
                          >
                            /contact
                          </Link>
                        </p>
                      </div>
                    </motion.div>
                  </section>
                </div>
              </div>
            </div>
          </section>

          {/* ==================== RELATED LINKS ==================== */}
          <section className="py-8 relative">
            <div className="max-w-[900px] mx-auto px-6 lg:px-12">
              <div className="flex flex-wrap justify-center gap-4">
                <Link 
                  to="/terms"
                  className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                >
                  Terms of Service
                </Link>
                <Link 
                  to="/disclosures"
                  className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                >
                  Disclosures
                </Link>
                <Link 
                  to="/contact"
                  className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
